/**
 * Repository Implementation: Tiering Settings
 *
 * Bridges the TieringSettingsApiService with the domain model.
 */

import { inject } from "inversify";

import type {
	FetchTieringSettingsParams,
	TieringPeriodDetail,
	TieringPeriodWritePayload,
	TieringSettingsItem,
	TieringSettingsPaginatedResult,
	TieringTierDetail,
} from "../../domain/model/TieringSettingsModel";
import type { ITieringSettingsRepository } from "../../domain/repository/ITieringSettingsRepository";
import type {
	TieringPeriodDetailDto,
	TieringPeriodDto,
	TieringTierDetailDto,
} from "../remote/response/TieringSettingsResponse";
import { TieringSettingsApiService } from "../remote/service/TieringSettingsApiService";

export class TieringSettingsRepositoryImpl implements ITieringSettingsRepository {
	@inject(TieringSettingsApiService)
	private readonly apiService!: TieringSettingsApiService;

	async fetchAll(): Promise<readonly TieringSettingsItem[]> {
		const response = await this.apiService.fetchPeriodsPaginated({
			page: 1,
			size: 50,
		});

		return response.data.map((period) => this.mapPeriodToListItem(period));
	}

	async fetchPaginated(
		params: FetchTieringSettingsParams,
	): Promise<TieringSettingsPaginatedResult> {
		const response = await this.apiService.fetchPeriodsPaginated(params);

		return {
			items: response.data.map((period) => this.mapPeriodToListItem(period)),
			paging: {
				currentPage: response.paging.currentPage,
				pageSize: response.paging.pageSize,
				totalRecords: response.paging.totalRecords,
				totalPages: response.paging.totalPages,
			},
		};
	}

	async fetchDetail(id: string): Promise<TieringPeriodDetail> {
		const dto = await this.apiService.fetchPeriodDetail(id);
		return this.mapDetailToDomain(dto);
	}

	async create(payload: TieringPeriodWritePayload): Promise<string> {
		const request = this.apiService.buildWritePayload(
			payload.year,
			payload.name,
			payload.isActive,
			payload.tiers.map((tier) => ({
				tierLevel: tier.tierLevel,
				tierName: tier.tierName,
				tierCode: tier.tierCode,
				minPoints: tier.minPoints,
				maxPoints: tier.maxPoints,
				directPointsMultiplier: tier.directPointsMultiplier,
				birthdayRewardPoints: tier.birthdayRewardPoints,
				dailyRedemptionLimit: tier.dailyRedemptionLimit,
				sortOrder: tier.sortOrder,
				badgeIcon: tier.badgeIcon,
				colorHex: tier.colorHex,
				metadata: tier.metadata,
			})),
		);

		const result = await this.apiService.createPeriod(request);
		return result.id;
	}

	async update(id: string, payload: TieringPeriodWritePayload): Promise<void> {
		const request = this.apiService.buildWritePayload(
			payload.year,
			payload.name,
			payload.isActive,
			payload.tiers.map((tier) => ({
				tierLevel: tier.tierLevel,
				tierName: tier.tierName,
				tierCode: tier.tierCode,
				minPoints: tier.minPoints,
				maxPoints: tier.maxPoints,
				directPointsMultiplier: tier.directPointsMultiplier,
				birthdayRewardPoints: tier.birthdayRewardPoints,
				dailyRedemptionLimit: tier.dailyRedemptionLimit,
				sortOrder: tier.sortOrder,
				badgeIcon: tier.badgeIcon,
				colorHex: tier.colorHex,
				metadata: tier.metadata,
			})),
		);

		await this.apiService.updatePeriod(id, request);
	}

	async toggleStatus(id: string, active: boolean): Promise<void> {
		await this.apiService.togglePeriodStatus(id, active);
	}

	async delete(id: string): Promise<void> {
		await this.apiService.deletePeriod(id);
	}

	private mapPeriodToListItem(period: TieringPeriodDto): TieringSettingsItem {
		return {
			id: period.id,
			period: String(period.period),
			createdBy: "System",
			createdAt: period.createdAt,
			updatedBy: "System",
			status: period.isActive ? "ongoing" : "ended",
			updatedAt: period.updatedAt,
		};
	}

	private mapDetailToDomain(dto: TieringPeriodDetailDto): TieringPeriodDetail {
		return {
			id: dto.id,
			year: dto.period.year,
			name: dto.period.name,
			validFrom: dto.period.valid_from,
			validUntil: dto.period.valid_until,
			isActive: dto.period.is_active,
			tiers: dto.tiers.map((tier) => this.mapTierToDomain(tier)),
		};
	}

	private mapTierToDomain(dto: TieringTierDetailDto): TieringTierDetail {
		return {
			id: dto.id,
			tierLevel: dto.tier_level,
			tierName: dto.tier_name,
			tierCode: dto.tier_code,
			minPoints: dto.min_points,
			maxPoints: dto.max_points,
			directPointsMultiplier: dto.direct_points_multiplier,
			birthdayRewardPoints: dto.birthday_reward_points,
			dailyRedemptionLimit: dto.daily_redemption_limit,
			badgeIcon: dto.badge_icon,
			colorHex: dto.color_hex,
			isActive: dto.is_active,
		};
	}
}
